import { DateTime } from "luxon";

import { ButtonInput } from "../shared/Inputs/ButtonInput";
import { IOptimiseResult, IOptimiseTrip } from "../../models/TripOptimisation";

interface PrintOptimisedRouteProps {
  result?: IOptimiseResult;
  selectedDay?: string;
  /** Trip display colours (hex), used for the printed route map's line and pins. */
  tripColours?: Record<string, string>;
  // Distinguishes multiple instances so their hidden print DOM ids don't clash.
  instanceId?: string;
  buttonLabel?: string;
  buttonSize?: "sm" | "md" | "lg";
}

interface ScheduleRow {
  kind: "depart" | "stop" | "break" | "unload" | "return";
  time: string;
  label: string;
  detail?: string;
  vehicle?: string;
  isVan?: boolean;
  notes?: string;
}

// A truck's trips for the day, chained in departure order (the backend already
// schedules them: unload between trips, break counters carried across).
interface TruckPlan {
  truckName: string;
  trips: IOptimiseTrip[];
}

const fmtISO = (iso?: string | null): string =>
  iso ? DateTime.fromISO(iso, { zone: "Europe/London" }).toFormat("HH:mm") : "";

const formatDriving = (seconds: number): string => {
  const mins = Math.max(Math.round(seconds / 60), 0);
  const hours = Math.floor(mins / 60);
  const remainder = mins % 60;
  return hours > 0
    ? `${hours}h ${remainder.toString().padStart(2, "0")}m`
    : `${remainder}m`;
};

/**
 * Printable route + schedule sheet, one page per truck, built from the backend's
 * optimise result (which already carries the chained day schedule). Ported from
 * the Adam dashboard's PrintOptimisedRoute; the static route map and turn-by-turn
 * directions are omitted because the Mapbox token stays server-side in Wilma.
 */
export default function PrintOptimisedRoute({
  result,
  selectedDay,
  tripColours,
  instanceId = "route",
  buttonLabel = "Print route + schedule",
  buttonSize = "md",
}: PrintOptimisedRouteProps) {
  const sectionId = `${instanceId}-print-route-section`;
  const frameId = `${instanceId}-route-frame`;

  const tripsWithStops = result?.trips.filter((trip) => trip.stops.length > 0) ?? [];

  // Group each truck's trips and order them by departure so multi-trip days
  // print as one chained schedule per truck.
  const plans: TruckPlan[] = [];
  tripsWithStops.forEach((trip) => {
    const name = trip.truckName ?? "Trip";
    let plan = plans.find((p) => p.truckName === name);
    if (!plan) {
      plan = { truckName: name, trips: [] };
      plans.push(plan);
    }
    plan.trips.push(trip);
  });
  plans.forEach((plan) =>
    plan.trips.sort((a, b) => (a.departIso ?? "").localeCompare(b.departIso ?? ""))
  );

  const buildTruckSummary = (plan: TruckPlan) => {
    const breaks: string[] = [];
    plan.trips.forEach((trip) => {
      trip.stops.forEach((stop) => {
        if (stop.breakBeforeIso) breaks.push(stop.breakBeforeIso);
      });
      if (trip.breakBeforeReturnIso) breaks.push(trip.breakBeforeReturnIso);
    });
    return {
      start: plan.trips[0]?.departIso ?? "",
      ret: plan.trips[plan.trips.length - 1]?.returnIso ?? "",
      breaks,
      drivingSeconds: plan.trips.reduce((sum, t) => sum + t.drivingSeconds, 0),
    };
  };

  const buildTruckSchedule = (plan: TruckPlan): ScheduleRow[] => {
    const rows: ScheduleRow[] = [];

    plan.trips.forEach((trip, tripIndex) => {
      if (trip.unloadBeforeIso) {
        rows.push({
          kind: "unload",
          time: fmtISO(trip.unloadBeforeIso),
          label: "Unload on site (30 min)",
        });
      }
      rows.push({
        kind: "depart",
        time: fmtISO(trip.departIso),
        label: tripIndex === 0 ? "Depart site" : `Depart site — trip ${tripIndex + 1}`,
      });

      trip.stops.forEach((stop, index) => {
        if (stop.breakBeforeIso) {
          rows.push({
            kind: "break",
            time: fmtISO(stop.breakBeforeIso),
            label: "45-minute break",
          });
        }
        rows.push({
          kind: "stop",
          time: fmtISO(stop.arriveIso),
          label: `${index + 1}. ${stop.vrm ?? "No VRM"}`,
          detail: stop.postcode ?? "",
          vehicle: [stop.make, stop.model].filter(Boolean).join(" "),
          isVan: stop.isVan,
          notes: [stop.driverNotes, stop.vehicleNotes]
            .filter((note) => note && note.trim().length > 0)
            .join(" — "),
        });
      });

      if (trip.breakBeforeReturnIso) {
        rows.push({
          kind: "break",
          time: fmtISO(trip.breakBeforeReturnIso),
          label: "45-minute break",
        });
      }

      const isLast = tripIndex === plan.trips.length - 1;
      rows.push({
        kind: "return",
        time: fmtISO(trip.returnIso),
        label: isLast ? "Return to site" : `Back at site — end of trip ${tripIndex + 1}`,
      });
    });

    return rows;
  };

  function printSection() {
    const content = document.getElementById(sectionId);
    const iframe = document.getElementById(frameId) as HTMLIFrameElement;
    const pri = iframe?.contentWindow;
    if (!content || !pri) return;

    pri.document.open();
    pri.document.write(`
      <html>
        <head>
          <title>Route schedule</title>
          <style>
            @page { size: portrait; margin: 12mm; }
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              font-family: 'Poppins', sans-serif;
            }
            .route-page { page-break-before: always; }
            .route-page:first-child { page-break-before: auto; }
            h2 { font-size: 18px; margin: 0 0 8px; }
            h3 { font-size: 14px; margin: 12px 0 4px; }
            .summary { display: flex; flex-wrap: wrap; gap: 4px 18px; margin: 0 0 10px; padding: 6px 10px; background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 6px; font-size: 13px; }
            .summary span { white-space: nowrap; }
            img { width: 100%; height: auto; border: 1px solid #d1d5db; border-radius: 6px; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th, td { border: 1px solid #d1d5db; padding: 6px 8px; font-size: 13px; text-align: left; }
            th { background: #dbeafe; }
            .time { width: 64px; font-weight: bold; white-space: nowrap; }
            .notes { white-space: pre-line; color: #374151; font-size: 12px; }
            .van { display: inline-block; margin-left: 6px; padding: 0 5px; border-radius: 4px; background: #1d4ed8; color: #fff; font-size: 10px; font-weight: bold; vertical-align: middle; }
            .break td { background: #fef3c7; font-weight: bold; }
            .unload td { background: #e0e7ff; font-weight: bold; }
            .endpoint td { background: #f3f4f6; font-weight: bold; }
          </style>
        </head>
        <body>${content.innerHTML}</body>
      </html>
    `);
    pri.document.close();
    pri.focus();

    // Wait for the static map images to load before printing (guarded so the
    // fallback timeout can't trigger a second print).
    let printed = false;
    const fire = () => {
      if (printed) return;
      printed = true;
      pri.print();
    };

    const images = Array.from(pri.document.images);
    if (images.length === 0) {
      fire();
      return;
    }
    let loaded = 0;
    const onDone = () => {
      loaded += 1;
      if (loaded >= images.length) fire();
    };
    images.forEach((img) => {
      if (img.complete) onDone();
      else {
        img.addEventListener("load", onDone);
        img.addEventListener("error", onDone);
      }
    });
    window.setTimeout(fire, 5000);
  }

  // The trip's static route map with the display colour swapped in.
  const mapUrlFor = (trip: IOptimiseTrip): string | null => {
    if (!trip.staticMapUrl) return null;
    const colourHex = (tripColours?.[trip.tripId] ?? "#2563eb").replace("#", "");
    return trip.staticMapUrl.split("__COLOUR__").join(colourHex);
  };

  const rowClass = (kind: ScheduleRow["kind"]): string => {
    if (kind === "break") return "break";
    if (kind === "unload") return "unload";
    if (kind === "depart" || kind === "return") return "endpoint";
    return "";
  };

  return (
    <>
      <ButtonInput
        label={buttonLabel}
        isSubmit={false}
        size={buttonSize}
        classes=""
        onClick={printSection}
        disabled={tripsWithStops.length === 0}
      />

      <div className="hidden" id={sectionId}>
        {plans.map((plan, planIndex) => {
          const schedule = buildTruckSchedule(plan);
          const summary = buildTruckSummary(plan);
          const multiTrip = plan.trips.length > 1;
          const driverName = Array.from(
            new Set(
              plan.trips
                .map((trip) => trip.driverName ?? "")
                .filter((name) => name.length > 0)
            )
          ).join(" / ");

          return (
            <div className="route-page" key={`${plan.truckName}-${planIndex}`}>
              <h2>
                {plan.truckName}
                {driverName ? ` — ${driverName}` : ""} — {selectedDay ?? ""}
              </h2>

              <div className="summary">
                <span>
                  Start: <b>{fmtISO(summary.start)}</b>
                </span>
                <span>
                  Est. return: <b>{fmtISO(summary.ret)}</b>
                </span>
                <span>
                  Break:{" "}
                  <b>
                    {summary.breaks.length > 0
                      ? summary.breaks.map((iso) => fmtISO(iso)).join(", ")
                      : "None"}
                  </b>
                </span>
                <span>
                  Driving: <b>{formatDriving(summary.drivingSeconds)}</b>
                </span>
              </div>

              {plan.trips.map((trip, tripIndex) => {
                const mapUrl = mapUrlFor(trip);
                if (!mapUrl) return null;
                return (
                  <div key={trip.tripId}>
                    {multiTrip && <h3>Trip {tripIndex + 1}</h3>}
                    <img
                      src={mapUrl}
                      alt={`${plan.truckName} trip ${tripIndex + 1} route`}
                    />
                  </div>
                );
              })}

              <table>
                <thead>
                  <tr>
                    <th className="time">Time</th>
                    <th>Stop</th>
                    <th>Vehicle</th>
                    <th>Postcode</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {schedule.map((row, i) => (
                    <tr key={i} className={rowClass(row.kind)}>
                      <td className="time">{row.time}</td>
                      <td>{row.label}</td>
                      <td>
                        {row.vehicle ?? ""}
                        {row.isVan && <span className="van">VAN</span>}
                      </td>
                      <td>{row.detail ?? ""}</td>
                      <td className="notes">{row.notes ?? ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
      <iframe title={frameId} id={frameId} className="h-0 w-0 absolute" />
    </>
  );
}
